/**
 * CFS product card behaviour (build spec §5).
 *
 * <cfs-product-card>: Add → − qty + stepper bound to the variant's cart line. Optimistic: the
 * number changes at once, taps are coalesced, one request sets the final quantity, and the card
 * rolls back and shows the cart's message (e.g. a MinMaxify limit) if the request fails.
 *
 * "Bought before" (T6.2, spec §14.3): for signed-in customers the layout renders the product ids
 * from their last 10 orders once (snippets/cfs-bought-before.liquid); matching cards get the badge,
 * keeping at most two badges (Offer/Multibuy > Pre-order > Bought before > New > Best seller).
 *
 * Pre-orders: a card for a product that keeps selling at zero stock carries data-preorder and data-stock;
 * when the stepper goes past the stock on hand it shows "N on pre-order" (the Add button keeps saying Add).
 *
 * Products with more than one variant have a plain "Choose options" link to the product page, no JS.
 */
import { CART_STATE_EVENT, getCartState, lineForVariant, setVariantQuantity } from '@theme/cfs-cart';

const COALESCE_MS = 300;

/** @type {Set<string> | null} */
let boughtBefore = null;
function boughtBeforeIds() {
  if (!boughtBefore) {
    try {
      const el = document.getElementById('CfsBoughtBefore');
      boughtBefore = new Set(el ? JSON.parse(el.textContent ?? '[]').map(String) : []);
    } catch {
      boughtBefore = new Set();
    }
  }
  return boughtBefore;
}
const MESSAGE_MS = 6000;

class CfsProductCard extends HTMLElement {
  /** @type {number} quantity shown on the card */
  #shown = 0;
  /** @type {number} quantity the shopper has asked for */
  #wanted = 0;
  /** @type {number} quantity the cart is known to hold */
  #confirmed = 0;
  #timer = 0;
  #inFlight = false;
  #messageTimer = 0;

  get variantId() {
    return this.dataset.variantId ?? '';
  }

  get productTitle() {
    return this.dataset.productTitle ?? '';
  }

  connectedCallback() {
    this.#markBoughtBefore();
    this.addButton = this.querySelector('[data-cfs-add]');
    if (!this.addButton) return;
    this.stepper = this.querySelector('[data-cfs-stepper]') ?? this.#buildStepper();

    this.qtyEl = this.stepper.querySelector('[data-cfs-qty]');
    this.increaseButton = this.stepper.querySelector('[data-cfs-increase]');
    this.decreaseButton = this.stepper.querySelector('[data-cfs-decrease]');
    this.messageEl = this.querySelector('[data-cfs-message]');
    this.preorderEl = this.querySelector('[data-cfs-preorder-note]');

    this.addButton.addEventListener('click', this.#onAdd);
    this.increaseButton?.addEventListener('click', this.#onIncrease);
    this.decreaseButton?.addEventListener('click', this.#onDecrease);
    document.addEventListener(CART_STATE_EVENT, this.#onCartState);

    this.#syncFromCart(getCartState(), false);
  }

  #markBoughtBefore() {
    const badges = this.querySelector('[data-cfs-badges]');
    if (!badges || badges.querySelector('[data-cfs-badge="bought"]')) return;
    if (!boughtBeforeIds().has(this.dataset.productId ?? '')) return;

    const badge = document.createElement('span');
    badge.className = 'cfs-badge cfs-badge--bought';
    badge.dataset.cfsBadge = 'bought';
    badge.textContent = 'Bought before';
    const offers = badges.querySelectorAll('[data-cfs-badge="offer"], [data-cfs-badge="multibuy"], [data-cfs-badge="preorder"]');
    if (offers.length) offers[offers.length - 1].after(badge);
    else badges.prepend(badge);
    // Max two: drop the lowest-priority badges (New, Best seller come last).
    const all = badges.querySelectorAll('[data-cfs-badge]');
    for (let i = all.length - 1; i >= 2; i--) {
      const dropped = all[i].getAttribute('data-cfs-badge');
      all[i].remove();
      // keep screen reader text in step with what is shown
      const sr = this.querySelector('[data-cfs-sr-badges]');
      if (sr && dropped === 'new') sr.textContent = (sr.textContent ?? '').replace(', new', '');
      if (sr && dropped === 'best') sr.textContent = (sr.textContent ?? '').replace(', best seller', '');
    }

    const title = this.querySelector('.cfs-product-card__title');
    let sr = this.querySelector('[data-cfs-sr-badges]');
    if (!sr && title) {
      sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.setAttribute('data-cfs-sr-badges', '');
      title.append(sr);
    }
    if (sr) sr.textContent = `${sr.textContent ?? ''}, bought before`;
  }

  /** Builds the − qty + stepper next to the Add button (not server-rendered, to keep cards small). */
  #buildStepper() {
    const title = this.productTitle;
    const icon = (/** @type {string} */ name, /** @type {number} */ size) =>
      `<svg class="cfs-icon" aria-hidden="true" focusable="false" width="${size}" height="${size}"><use href="#cfs-i-${name}"/></svg>`;
    const stepper = document.createElement('div');
    stepper.className = 'cfs-stepper';
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', `${title} in basket`);
    stepper.dataset.cfsStepper = '';
    stepper.hidden = true;
    stepper.innerHTML =
      `<button type="button" class="cfs-stepper__button" data-cfs-decrease>${icon('minus', 16)}</button>` +
      '<span class="cfs-stepper__qty" data-cfs-qty>0</span>' +
      `<button type="button" class="cfs-stepper__button" data-cfs-increase>${icon('plus', 18)}</button>`;
    /** @type {HTMLElement} */ (stepper.lastElementChild).setAttribute('aria-label', `Add one more ${title}`);
    /** @type {HTMLElement} */ (stepper.firstElementChild).setAttribute('aria-label', `Remove one ${title}`);
    this.addButton?.after(stepper);
    return stepper;
  }

  disconnectedCallback() {
    document.removeEventListener(CART_STATE_EVENT, this.#onCartState);
    clearTimeout(this.#timer);
    clearTimeout(this.#messageTimer);
  }

  #onCartState = () => {
    // Ignore cart broadcasts while this card has its own change pending.
    if (this.#inFlight || this.#timer) return;
    this.#syncFromCart(getCartState(), false);
  };

  /**
   * @param {import('./cfs-cart.js').CfsCart} _cart
   * @param {boolean} moveFocus
   */
  #syncFromCart(_cart, moveFocus) {
    const quantity = lineForVariant(this.variantId)?.quantity ?? 0;
    this.#confirmed = quantity;
    this.#wanted = quantity;
    this.#render(quantity, moveFocus, false);
  }

  #onAdd = () => this.#request(Math.max(1, this.#shown + 1), true);
  #onIncrease = () => this.#request(this.#shown + 1, false);
  #onDecrease = () => {
    this.#setLimited(false);
    this.#clearMessage();
    this.#request(Math.max(0, this.#shown - 1), this.#shown - 1 <= 0);
  };

  /**
   * @param {number} quantity
   * @param {boolean} moveFocus - Move focus when the control swaps (Add ↔ stepper).
   */
  #request(quantity, moveFocus) {
    this.#clearMessage();
    this.#wanted = quantity;
    this.#render(quantity, moveFocus);

    clearTimeout(this.#timer);
    // The first add goes straight away; stepper taps are coalesced into one request.
    const delay = this.#confirmed === 0 && quantity === 1 ? 0 : COALESCE_MS;
    this.#timer = window.setTimeout(() => {
      this.#timer = 0;
      this.#flush();
    }, delay);
  }

  async #flush() {
    if (this.#inFlight || this.#wanted === this.#confirmed) return;

    this.#inFlight = true;
    this.setAttribute('aria-busy', 'true');
    const target = this.#wanted;

    try {
      const cart = await setVariantQuantity({ variantId: this.variantId, quantity: target, source: this });
      this.#confirmed =
        cart.items.find((line) => String(line.variant_id) === String(this.variantId))?.quantity ?? 0;
      if (this.#confirmed !== target && this.#wanted === target) {
        // The cart accepted fewer than asked (stock limit): show what is really in the basket.
        this.#wanted = this.#confirmed;
        this.#render(this.#confirmed, false, false);
        this.#showMessage(`Only ${this.#confirmed} available`);
      }
    } catch (error) {
      // Roll back to what the cart holds and explain, unless the shopper has tapped again since.
      this.#confirmed = lineForVariant(this.variantId)?.quantity ?? 0;
      if (this.#wanted === target) {
        this.#wanted = this.#confirmed;
        this.#render(this.#confirmed, document.activeElement?.closest('cfs-product-card') === this, false);
      }
      this.#showMessage(/** @type {Error} */ (error)?.message || 'Could not update your basket', Boolean(/** @type {any} */ (error)?.detail?.limit));
      if (/** @type {any} */ (error)?.detail?.limit) this.#setLimited(true);
    } finally {
      this.#inFlight = false;
      this.removeAttribute('aria-busy');
      // More taps arrived while the request was running.
      if (this.#wanted !== this.#confirmed && !this.#timer) this.#flush();
    }
  }

  /**
   * @param {number} quantity
   * @param {boolean} moveFocus
   * @param {boolean} [announce=true] - Announce the new quantity (only for the shopper's own taps).
   */
  #render(quantity, moveFocus, announce = true) {
    const wasStepper = this.#shown > 0;
    const isStepper = quantity > 0;
    this.#shown = quantity;

    // CFS: write only what changed. Every card re-renders on each basket update, and each redundant
    // write is a DOM mutation that page-wide observers (apps, the drawer's "Little extras") react to.
    if (this.qtyEl && this.qtyEl.textContent !== String(quantity)) this.qtyEl.textContent = String(quantity);
    if (this.stepper.hidden !== !isStepper) this.stepper.hidden = !isStepper;
    if (this.addButton.hidden !== isStepper) this.addButton.hidden = isStepper;
    this.classList.toggle('cfs-product-card--in-basket', isStepper);

    // Pre-order products with stock on hand: say how many of the shopper's quantity are on pre-order.
    const preorder = this.preorderEl ? Math.max(0, quantity - (Number(this.dataset.stock) || 0)) : 0;
    if (this.preorderEl) {
      const note = preorder > 0 ? `${preorder} on pre-order` : '';
      if (this.preorderEl.textContent !== note) this.preorderEl.textContent = note;
    }

    if (this.decreaseButton) {
      const label = quantity === 1 ? `Remove ${this.productTitle} from basket` : `Remove one ${this.productTitle}`;
      if (this.decreaseButton.getAttribute('aria-label') !== label) this.decreaseButton.setAttribute('aria-label', label);
    }

    if (moveFocus && wasStepper !== isStepper) {
      (isStepper ? this.increaseButton : this.addButton)?.focus();
    }

    if (announce) {
      this.#announce(isStepper ? `${quantity} in basket${preorder ? `, ${preorder} on pre-order` : ''}` : 'Removed from basket');
    }
  }

  /** @param {string} text */
  #announce(text) {
    if (!this.messageEl || this.messageEl.dataset.error) return;
    this.messageEl.classList.add('visually-hidden');
    this.messageEl.textContent = text;
  }

  /**
   * @param {string} text
   * @param {boolean} [persistent] - Keep the message (e.g. a category limit) instead of fading it.
   */
  #showMessage(text, persistent = false) {
    if (!this.messageEl) return;
    clearTimeout(this.#messageTimer);
    this.messageEl.classList.remove('visually-hidden');
    this.messageEl.dataset.error = 'true';
    this.messageEl.textContent = text;
    if (!persistent) this.#messageTimer = window.setTimeout(() => this.#clearMessage(), MESSAGE_MS);
  }

  /** MinMaxify limit reached for this product's category: no more increases on this card. */
  /** @param {boolean} limited */
  #setLimited(limited) {
    this.classList.toggle('cfs-product-card--limited', limited);
    if (this.increaseButton instanceof HTMLButtonElement) this.increaseButton.disabled = limited;
  }

  #clearMessage() {
    if (!this.messageEl) return;
    delete this.messageEl.dataset.error;
    this.messageEl.textContent = '';
  }
}

if (!customElements.get('cfs-product-card')) customElements.define('cfs-product-card', CfsProductCard);
